import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Table, Badge, Button, Form, ListGroup, Spinner, Modal } from "react-bootstrap";
import Select from "react-select";
import { useNavigate, useParams } from 'react-router-dom';
import { CategoryDetailEntity } from "../../dto/category/CategoryDetailEntity";
import { onAddAttributeChoiceToCategory, onAttributeChoiceList, onAttributeList, onBrandList, onCategoryAttributeCreate, onCategoryAttributeUpdate, onCategoryBasicUpdate, onCategoryBrandCreate, onCategoryDetail, onCategoryMediaDelete, onCategoryMediaUpload, onRemoveAttributeChoiceFromCategory, onRemoveBrandFromCategory } from "../../controller/CategoryController";
import AuthUtility from "../../util/AuthUtility";
import { CategoryMediaEntity } from "../../dto/category/CategoryMediaEntity";
import { AttributeEntity } from "../../dto/attribute/AttributeEntity";
import { AttributeChoiceEntity } from "../../dto/attribute/AttributeChoiceEntity";
import { CategoryAttributeEntity } from "../../dto/category/CategoryAttributeEntity";
import { CategoryAttributeChoiceEntity } from "../../dto/category/CategoryAttributeChoiceEntity";
import Delete from '@mui/icons-material/Delete';
import Add from '@mui/icons-material/Add';
import toast, { Toaster } from 'react-hot-toast';
import { BrandEntity } from "../../dto/brand/BrandEntity";
import { CategoryBrandEntity } from "../../dto/category/CategoryBrandEntity";
import { CategoryEntity } from "../../dto/category/CategoryEntity";

type CategoryParams = {
  categoryId: string;
};

interface ChoiceOption {
  value: number;
  label: string;
}

// ================== COMPONENT ==================
const CategoryDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams<CategoryParams>();
  const [categoryDetail, setCategoryDetail] = useState<CategoryDetailEntity>({});

  const [showMediaModal, setShowMediaModal] = useState(false);
  const [editingMedia, setEditingMedia] = useState(false);

  const [newMedia, setNewMedia] = useState<CategoryMediaEntity>({
    role: "ICON",
    status: "ACTIVE",
  });

  const [showAttributeModal, setShowAttributeModal] = useState(false);
  const [showAttributeChoiceModal, setShowAttributeChoiceModal] = useState(false);
  const [showBrandModal, setshowBrandModal] = useState(false);

  const [attributeList, setAttributeList] = useState<AttributeEntity[]>([]);
  const [choiceList, setChoiceList] = useState<AttributeChoiceEntity[]>([]);
  const [newChoiceList, setNewChoiceList] = useState<AttributeChoiceEntity[]>([]);
  const [brandList, setBrandList] = useState<BrandEntity[]>([]);

  const [newAttribute, setNewAttribute] = useState<CategoryAttributeEntity | undefined>({});
  const [newAttributeChoice, setNewAttributeChoice] = useState<CategoryAttributeEntity | undefined>({});
  const [newCategoryBrand, setNewCategoryBrand] = useState<CategoryBrandEntity>({
    brands: [],
  });

  const [attributeSearch, setAttributeSearch] = useState("");

  const [basicInfo, setBasicInfo] = useState<CategoryEntity>({
    name: "",
    sortOrder: 0,
    status: "",
    freightRequiredFlag: false,
    onWebTop: false,
    leafFlag: false,
    listingFeeAmount: 0,        // ← add this
    listingFeeCurrency: "MNT",  // ← optional, add if you want currency too
  });

  const [uploading, setUploading] = useState(false);

  const choiceOptions: ChoiceOption[] = choiceList.map(c => ({
    value: c.id!,
    label: c.valueText || c.status || `Choice ${c.id}`,
  }));

  const newChoiceOptions: ChoiceOption[] = newChoiceList.map(c => ({
    value: c.id!,
    label: c.valueText || c.status || `Choice ${c.id}`,
  }));

  const newBrandOptions: ChoiceOption[] = brandList.map(c => ({
    value: c.id!,
    label: c.name || c.status || `Brand ${c.id}`,
  }));

  useEffect(() => {
    if (!AuthUtility.isUserLoggedIn()) {
      navigate("/login");
      window.location.reload();
    } else {
      categoryDetailGet();
    }
  }, []);

  const categoryDetailGet = async () => {
    const response = await onCategoryDetail(parseInt(categoryId!));
    const data = response.data;
    if (data !== undefined) {

      // 🔐 Load media previews with Authorization header
      // const mediasWithPreview = data.medias
      // ? await Promise.all(
      //     data.medias.map(async (m: any) => ({
      //       ...m,
      //       preview: m.fileUuid
      //         ? await ApiUtility.loadImageWithAuth(m.fileUuid)
      //         : undefined,
      //       createdAt: m.createdAt ? new Date(m.createdAt) : undefined,
      //       updatedAt: m.updatedAt ? new Date(m.updatedAt) : undefined,
      //     }))
      //   )
      // : [];

      setCategoryDetail({
        category: data.category
          ? {
            ...data.category,
            createdAt: data.category.createdAt
              ? new Date(data.category.createdAt)
              : undefined,
            updatedAt: data.category.updatedAt
              ? new Date(data.category.updatedAt)
              : undefined,
          }
          : undefined,

        // medias: mediasWithPreview,   // ✅ use authenticated previews
        medias: data.medias?.map((m) =>
        ({
          ...m, preview: 'https://ns9.checkin.mn/v1/file/api/files/' + m.fileUuid,
          createdAt: m.createdAt ? new Date(m.createdAt) : undefined,
          updatedAt: m.updatedAt ? new Date(m.updatedAt) : undefined,
        })),

        attributes: data.attributes,
        brands: data.brands,
      });

      setBasicInfo({
        name: data.category?.name || "",
        sortOrder: data.category?.sortOrder || 0,
        status: data.category?.status || "",
        freightRequiredFlag: !!data.category?.freightRequiredFlag,
        onWebTop: !!data.category?.onWebTop,
        leafFlag: !!data.category?.leafFlag,
        listingFeeAmount: data.category?.listingFeeAmount ?? 0,   // ← add
        listingFeeCurrency: data.category?.listingFeeCurrency || "MNT", // ← optional
      });
    }
  }

  // ================= ADD MEDIA HANDLER =================
  const handleSaveMedia = async () => {
    const formData = new FormData();

    if (editingMedia) {
      // ===== UPDATE MODE =====
      formData.append(
        "categoryMedia",
        new Blob([JSON.stringify(newMedia)], {
          type: "application/json"
        })
      );
    } else {
      // ===== ADD MODE =====
      if (!newMedia.data) {
        alert("File is required");
        return;
      }

      const mediaPayload = {
        ...newMedia,
        categoryId: parseInt(categoryId!), // ensure categoryId is present
      };

      formData.append(
        "categoryMedia",
        new Blob([JSON.stringify(mediaPayload)], { type: "application/json" })
      );

      formData.append("data", newMedia.data);
    }

    try {
      setUploading(true);

      // TODO: replace with your real API
      const res = await onCategoryMediaUpload(formData);
      if (res.status == 0) {
        toast.success(res.msg);
      } else {
        toast.error(res.msg);
      }

      // reset + close
      setShowMediaModal(false);
      setEditingMedia(false);
      setNewMedia({ role: "ICON", status: "ACTIVE" });

      // reload list
      categoryDetailGet();

    } catch (e) {
      console.error(e);
      toast.error("Media upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleAttributeChange = async (attributeId: number) => {
    const attribute = attributeList.find(a => a.id === attributeId);

    if (!attribute) return;

    // set selected attribute (without choices yet)
    setNewAttribute({
      attributeId: attribute.id,        // ✅ this is the key fix
      attribute: attribute,             // keep full attribute object if you need it
      requiredFlag: false,
      multipleAllowed: false,
      filterableFlag: false,
      sortOrder: 1,
      choices: [],
    });

    setChoiceList([]);

    try {
      // TODO: replace with your real API
      const res = await onAttributeChoiceList(undefined, attributeId);
      if (res.status == 1) {
        toast.error(res.msg);
      }

      // available choices for UI
      setChoiceList(res.data.beans);

    } catch (e) {
      console.error(e);
      toast.error("Failed to load attribute choices");
    }
  };

  const removeAttributeChoiceFromCategory = async (attributeId?: number, choiceId?: number) => {
    const confirmed = window.confirm("Are you sure you want to delete this attribute choice?");
    if (!confirmed) return;

    const choicePayload: CategoryAttributeChoiceEntity = {
      categoryId: parseInt(categoryId!),
      attributeId: attributeId,
      attributeChoiceId: choiceId
    };

    try {
      // TODO: replace with your real API
      const res = await onRemoveAttributeChoiceFromCategory(choicePayload);
      if (res.status == 0) {
        toast.success(res.msg);
      } else {
        toast.error(res.msg);
      }

      categoryDetailGet();
    } catch (e) {
      console.error(e);
      toast.error("Failed to remove attribute choices");
    }
  }

  const handleNewChoiceAttributeChange = async (attributeId?: number) => {

    setNewAttributeChoice({
      attributeId: attributeId
    });

    setNewChoiceList([]);

    try {
      // TODO: replace with your real API
      const res = await onAttributeChoiceList(undefined, attributeId);
      if (res.status == 0) {
        toast.success(res.msg);
      } else {
        toast.error(res.msg);
      }

      // available choices for UI
      setNewChoiceList(res.data.beans);

    } catch (e) {
      console.error(e);
      toast.error("Failed to load attribute choices");
    }

    setShowAttributeChoiceModal(true);
  }

  const removeBrandFromCategory = async (brandId?: number) => {
    const confirmed = window.confirm("Are you sure you want to delete this brand?");
    if (!confirmed) return;

    const brandPayload = {
      categoryId: parseInt(categoryId!),
      brandId: brandId
    };

    try {
      // TODO: replace with your real API
      const res = await onRemoveBrandFromCategory(brandPayload);
      if (res.status == 0) {
        toast.success(res.msg);
      } else {
        toast.error(res.msg);
      }

      categoryDetailGet();
    } catch (e) {
      console.error(e);

      toast.error("Failed to remove attribute choices");
    }
  }

  const updateAttributeField = async (
    attributeId: number,
    field: "requiredFlag" | "multipleAllowed" | "filterableFlag" | "sortOrder",
    value: boolean | number
  ) => {
    // 1. Optimistic UI update
    updateAttributeFieldLocal(attributeId, field, value);

    const payload = {
      categoryId: parseInt(categoryId!),
      attributeId: attributeId,
      [field]: value,
    };

    try {
      const res = await onCategoryAttributeUpdate(payload);
      if (res.status == 0) {
        toast.success(res.msg);
      } else {
        toast.error(res.msg);
      }

      categoryDetailGet();
    } catch (e) {
      console.error(e);
      // 2. Revert by reloading from backend
      toast.error("Failed to update attribute field");
    }
  };

  const updateAttributeFieldLocal = (
    attributeId: number,
    field: "requiredFlag" | "multipleAllowed" | "filterableFlag" | "sortOrder",
    value: boolean | number
  ) => {
    setCategoryDetail(prev => {
      if (!prev.attributes) return prev;

      return {
        ...prev,
        attributes: prev.attributes.map(attr =>
          attr.attributeId === attributeId
            ? { ...attr, [field]: value }
            : attr
        ),
      };
    });
  };

  const handleDeleteMedia = async (mediaId?: number) => {
    if (!mediaId) return;

    // 1. Confirm first
    const confirmed = window.confirm("Are you sure you want to delete this media?");
    if (!confirmed) return;

    try {
      // TODO: replace with your real API
      const res = await onCategoryMediaDelete({
        categoryId: parseInt(categoryId!),
        id: mediaId,
      });

      if (res.status === 0) {
        toast.success(res.msg);
      } else {
        toast.error(res.msg);
      }

      // Reload list
      categoryDetailGet();

    } catch (e) {
      console.error(e);
      toast.error("Failed to delete media");
    }
  };

  const handleSaveBasicInfo = async () => {
    try {
      const payload = {
        id: parseInt(categoryId!),
        ...basicInfo,
      };

      // TODO: replace with your real API
      const res = await onCategoryBasicUpdate(payload);

      if (res.status === 0) {
        toast.success(res.msg);
        categoryDetailGet(); // reload from backend
      } else {
        toast.error(res.msg);
      }

    } catch (e) {
      console.error(e);
      toast.error("Failed to update basic information");
    }
  };

  return (
    <Container fluid className="py-3">
      <div><Toaster position="top-right" reverseOrder={false} /></div>
      {/* ============ BASIC INFO ============ */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Basic Information</span>
          <Button size="sm" onClick={handleSaveBasicInfo}>
            Save
          </Button>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Category Name</Form.Label>
                <Form.Control
                  value={basicInfo.name}
                  onChange={(e) =>
                    setBasicInfo(prev => ({ ...prev, name: e.target.value }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  value={basicInfo.sortOrder}
                  onChange={(e) =>
                    setBasicInfo(prev => ({ ...prev, sortOrder: Number(e.target.value) }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Listing Fee Amount</Form.Label>
                <Form.Control
                  type="number"
                  value={basicInfo.listingFeeAmount ?? 0}
                  onChange={(e) =>
                    setBasicInfo(prev => ({ ...prev, listingFeeAmount: Number(e.target.value) }))
                  }
                />
              </Form.Group>
              <div>Created At: {categoryDetail.category?.createdAt?.toLocaleDateString() || '-'}</div>
              <div>Updated At: {categoryDetail.category?.updatedAt?.toLocaleDateString() || '-'}</div>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={basicInfo.status}
                  onChange={(e) =>
                    setBasicInfo(prev => ({ ...prev, status: e.target.value }))
                  }
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="HIDDEN">HIDDEN</option>
                  <option value="DISABLED">DISABLED</option>
                </Form.Select>
              </Form.Group>
              <Form.Check
                label="Freight Required"
                checked={!!basicInfo.freightRequiredFlag}
                onChange={(e) =>
                  setBasicInfo(prev => ({ ...prev, freightRequiredFlag: e.target.checked }))
                }
              />
              <Form.Check
                label="On Web Top"
                checked={!!basicInfo.onWebTop}
                onChange={(e) =>
                  setBasicInfo(prev => ({ ...prev, onWebTop: e.target.checked }))
                }
              />
              <Form.Check label="Leaf Category" checked={!!categoryDetail.category?.leafFlag} readOnly />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ============ MEDIA ============ */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between">
          <span>Category Media ({categoryDetail?.medias?.length || 0})</span>
          <Button size="sm" onClick={() => {
            setEditingMedia(false);
            setNewMedia({ role: "ICON", status: "ACTIVE" });
            setShowMediaModal(true);
          }}>
            + Add Media
          </Button>
        </Card.Header>
        <Card.Body>
          <Table bordered hover>
            <thead>
              <tr>
                <th>Preview</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categoryDetail?.medias?.map(m => (
                <tr key={m.id}>
                  <td><img src={m.preview} alt={m.role} width={80} /></td>
                  <td>{m.role}</td>
                  <td><Badge bg={m.status === 'ACTIVE' ? 'success' : 'secondary'}>{m.status}</Badge></td>
                  <td>{m.createdAt?.toLocaleDateString()}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => {
                        setEditingMedia(true);

                        setNewMedia({
                          id: m.id,
                          role: m.role,          // ✅ preload role
                          status: m.status,      // ✅ preload status
                          preview: m.preview,   // existing image
                          data: undefined,      // no new file yet
                        });

                        setShowMediaModal(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDeleteMedia(m.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* ============ ATTRIBUTES ============ */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between">
          <span>Attributes ({categoryDetail?.attributes?.length || 0})</span>
          <Button
            size="sm"
            onClick={async () => {
              setShowAttributeModal(true);

              // reset
              setNewAttribute(undefined);
              setChoiceList([]);

              try {
                // TODO: replace with your API
                const res = await onAttributeList();
                setAttributeList(res.data.beans);

              } catch (e) {
                console.error(e);
                alert("Failed to load attributes");
              }
            }}
          >
            + Add Attribute
          </Button>
        </Card.Header>
        <Card.Body>
          <Form.Control
            className="mb-2"
            placeholder="Search attribute or choice..."
            value={attributeSearch}
            onChange={(e) => setAttributeSearch(e.target.value)}
          />
          <Table bordered hover>
            <thead>
              <tr>
                <th>Attribute</th>
                <th>Required</th>
                <th>Multiple</th>
                <th>Filterable</th>
                <th>Sort Order</th>
                <th>Choices</th>
              </tr>
            </thead>
            <tbody>
              {categoryDetail?.attributes
                ?.filter(a => {
                  const keyword = attributeSearch.toLowerCase().trim();
                  if (!keyword) return true;

                  const attributeNameMatch =
                    a.attribute?.name?.toLowerCase().includes(keyword);

                  const choiceMatch =
                    a.choices?.some(c =>
                      c.choice?.valueText?.toLowerCase().includes(keyword)
                    );

                  return attributeNameMatch || choiceMatch;
                })
                .map(a => (
                  <React.Fragment key={a.attributeId}>
                    <tr>
                      <td>{a.attribute?.name}</td>
                      <td>
                        <Form.Check
                          checked={!!a.requiredFlag}
                          onChange={(e) =>
                            updateAttributeField(a.attributeId!, "requiredFlag", e.target.checked)
                          }
                        />
                      </td>

                      <td>
                        <Form.Check
                          checked={!!a.multipleAllowed}
                          onChange={(e) =>
                            updateAttributeField(a.attributeId!, "multipleAllowed", e.target.checked)
                          }
                        />
                      </td>

                      <td>
                        <Form.Check
                          checked={!!a.filterableFlag}
                          onChange={(e) =>
                            updateAttributeField(a.attributeId!, "filterableFlag", e.target.checked)
                          }
                        />
                      </td>
                      <td style={{ width: 100 }}>
                        <Form.Control
                          type="number"
                          size="sm"
                          value={a.sortOrder ?? 0}
                          onChange={(e) => {
                            const newValue = Number(e.target.value);
                            updateAttributeField(a.attributeId!, "sortOrder", newValue);
                          }}
                        />
                      </td>
                      <td>
                        {
                          a.choices !== null &&
                          a.choices!.map((choice, choiceIdx) => {
                            return (
                              <Row key={`${choiceIdx}`}>
                                <span>
                                  - {choice.choice?.valueText}
                                  <a onClick={() => removeAttributeChoiceFromCategory(choice.attributeId, choice.attributeChoiceId)}
                                    style={{ float: "right" }}>
                                    <Delete />
                                  </a>
                                </span>
                              </Row>
                            )
                          })
                        }
                        <p>
                          - <a onClick={() => handleNewChoiceAttributeChange(a.attributeId)}>
                            <Add />
                          </a>
                        </p>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* ============ BRANDS ============ */}
      <Card>
        <Card.Header className="d-flex justify-content-between">
          Associated Brands ({categoryDetail?.brands?.length || 0})
          <Button
            size="sm"
            onClick={async () => {
              setshowBrandModal(true);

              // reset
              setNewCategoryBrand({
                brands: []
              });

              try {
                // TODO: replace with your API
                const res = await onBrandList();
                setBrandList(res.data.beans);

              } catch (e) {
                console.error(e);
                alert("Failed to load attributes");
              }
            }}
          >
            + Add Brand
          </Button>
        </Card.Header>
        <Card.Body>
          <Table bordered hover>
            <thead>
              <tr>
                <th>Brand</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {categoryDetail?.brands?.map(a => (
                <React.Fragment key={a.brandId}>
                  <tr>
                    <td>{a.brand?.name}</td>
                    <td>
                      <a onClick={() => removeBrandFromCategory(a.brandId)}>
                        <Delete />
                      </a>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* ============ ADD MEDIA MODAL ============ */}
      <Modal show={showMediaModal}
        onHide={() => {
          setShowMediaModal(false);
          setNewMedia({ role: "ICON", status: "ACTIVE" });
        }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingMedia ? "Edit Category Media" : "Add Category Media"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* ROLE */}
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={newMedia.role || "ICON"}
                onChange={(e) =>
                  setNewMedia({ ...newMedia, role: e.target.value })
                }
              >
                <option value="ICON">ICON</option>
                <option value="COVER">COVER</option>
                <option value="BANNER">BANNER</option>
              </Form.Select>
            </Form.Group>

            {/* STATUS */}
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={newMedia.status || "ACTIVE"}
                onChange={(e) =>
                  setNewMedia({ ...newMedia, status: e.target.value })
                }
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="DRAFT">DRAFT</option>
                <option value="DISABLED">DISABLED</option>
                <option value="DELETED">DELETED</option>
              </Form.Select>
            </Form.Group>

            {/* FILE */}
            <Form.Group>
              <Form.Label>File</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    const file = files[0];

                    setNewMedia({
                      ...newMedia,
                      data: file,
                      preview: URL.createObjectURL(file),
                    });
                  }
                }}
              />
            </Form.Group>

            {/* PREVIEW */}
            {newMedia.preview && (
              <div className="mt-3 text-center">
                <Badge bg="info" className="mb-2">{newMedia.role}</Badge>
                <br />
                <img
                  src={newMedia.preview}
                  alt="preview"
                  style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 6 }}
                />
              </div>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleSaveMedia}
            disabled={uploading}
          >
            {uploading ? <Spinner size="sm" /> : "Upload"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ ADD ATTRIBUTE MODAL ============ */}
      <Modal
        show={showAttributeModal}
        onHide={() => setShowAttributeModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Attribute to Category</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>

            {/* ATTRIBUTE SELECT */}
            <Form.Select
              value={newAttribute?.attributeId || ""}
              onChange={(e) => handleAttributeChange(Number(e.target.value))}
            >
              <option value="">-- Select Attribute --</option>
              {attributeList.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.code})
                </option>
              ))}
            </Form.Select>

            {/* CHOICE MULTI SELECT */}
            <Form.Group>
              <Form.Label>Attribute Choices</Form.Label>

              <Select
                isMulti
                isDisabled={!newAttribute}
                options={choiceOptions}
                placeholder="Select attribute choices..."

                // Only show user-selected choices
                value={
                  (newAttribute?.choices || []).map(c => ({
                    value: c.attributeChoiceId!, // maps to AttributeChoiceEntity.id
                    label: c.choice?.valueText || `Choice ${c.attributeChoiceId}`,
                  }))
                }

                onChange={(selectedOptions) => {
                  const selectedIds = (selectedOptions || []).map(opt => opt.value);

                  // Map selected IDs to CategoryAttributeChoiceEntity
                  const selectedChoices: CategoryAttributeChoiceEntity[] = choiceList
                    .filter(c => selectedIds.includes(c.id!))
                    .map(c => ({
                      attributeChoiceId: c.id!,
                      choice: c,
                    }));

                  setNewAttribute(prev => prev ? { ...prev, choices: selectedChoices } : prev);
                }}
              />
            </Form.Group>

            <Row className="mt-3">
              {/* REQUIRED */}
              <Col md={4}>
                <Form.Check
                  label="Required"
                  checked={!!newAttribute?.requiredFlag}
                  onChange={(e) =>
                    setNewAttribute(prev => prev ? ({
                      ...prev,
                      requiredFlag: e.target.checked,
                    }) : prev)
                  }
                />
              </Col>

              {/* MULTIPLE ALLOWED */}
              <Col md={4}>
                <Form.Check
                  label="Multiple Allowed"
                  checked={!!newAttribute?.multipleAllowed}
                  onChange={(e) =>
                    setNewAttribute(prev => prev ? ({
                      ...prev,
                      multipleAllowed: e.target.checked,
                    }) : prev)
                  }
                />
              </Col>

              {/* FILTERABLE */}
              <Col md={4}>
                <Form.Check
                  label="Filterable"
                  checked={!!newAttribute?.filterableFlag}
                  onChange={(e) =>
                    setNewAttribute(prev => prev ? ({
                      ...prev,
                      filterableFlag: e.target.checked,
                    }) : prev)
                  }
                />
              </Col>
            </Row>

            {/* SORT ORDER */}
            <Form.Group className="mt-3">
              <Form.Label>Sort Order</Form.Label>
              <Form.Control
                type="number"
                value={newAttribute?.sortOrder ?? 1}
                onChange={(e) =>
                  setNewAttribute(prev => prev ? ({
                    ...prev,
                    sortOrder: Number(e.target.value),
                  }) : prev)
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAttributeModal(false)}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={async () => {
              if (!newAttribute?.attributeId) {
                alert("Attribute is required");
                return;
              }

              try {
                const attributePayload = {
                  ...newAttribute,
                  categoryId: parseInt(categoryId!), // ensure categoryId is present
                };

                // TODO: replace with your real API
                const res = await onCategoryAttributeCreate(attributePayload);
                if (res.status === 0) {
                  toast.success(res.msg);
                  categoryDetailGet(); // reload from backend
                  setShowAttributeModal(false);
                } else {
                  toast.error(res.msg);
                }

              } catch (e) {
                console.error(e);
                alert("Failed to add attribute");
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ ADD ATTRIBUTE CHOICE MODAL ============ */}
      <Modal
        show={showAttributeChoiceModal}
        onHide={() => setShowAttributeChoiceModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Attribute Choice to Category</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* CHOICE MULTI SELECT */}
            <Form.Group>
              <Form.Label>Attribute Choices</Form.Label>

              <Select
                isMulti
                isDisabled={!newAttributeChoice}
                options={newChoiceOptions}
                placeholder="Select attribute choices..."
                onChange={(selectedOptions) => {
                  const selectedIds = (selectedOptions || []).map(opt => opt.value);

                  // Map selected IDs to CategoryAttributeChoiceEntity
                  const selectedChoices: CategoryAttributeChoiceEntity[] = newChoiceList
                    .filter(c => selectedIds.includes(c.id!))
                    .map(c => ({
                      attributeChoiceId: c.id!,
                      choice: c,
                    }));

                  setNewAttributeChoice(prev => prev ? { ...prev, choices: selectedChoices } : prev);
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAttributeChoiceModal(false)}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={async () => {
              if (!newAttributeChoice?.attributeId) {
                alert("Attribute is required");
                return;
              }

              try {
                const attributeChoicePayload = {
                  ...newAttributeChoice,
                  categoryId: parseInt(categoryId!), // ensure categoryId is present
                };

                // TODO: replace with your real API
                const res = await onAddAttributeChoiceToCategory(attributeChoicePayload);
                if (res.status === 0) {
                  toast.success(res.msg);
                  categoryDetailGet(); // reload from backend
                  setShowAttributeChoiceModal(false);
                } else {
                  toast.error(res.msg);
                }

              } catch (e) {
                console.error(e);
                alert("Failed to add attribute");
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ ADD ATTRIBUTE CHOICE MODAL ============ */}
      <Modal
        show={showBrandModal}
        onHide={() => setshowBrandModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Brand to Category</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* CHOICE MULTI SELECT */}
            <Form.Group>
              <Form.Label>Brands</Form.Label>

              <Select
                isMulti
                // isDisabled={!newCategoryBrand}
                options={newBrandOptions}
                placeholder="Select brand..."
                onChange={(selectedOptions) => {
                  const selectedIds = (selectedOptions || []).map(opt => opt.value);

                  // Map selected IDs to CategoryAttributeChoiceEntity
                  const selectedBrands: BrandEntity[] = brandList
                    .filter(b => selectedIds.includes(b.id!))
                    .map(b => ({
                      id: b.id!,
                      brand: b,
                    }));

                  // setNewCategoryBrand(prev => prev ? { ...prev, brands: selectedBrands } : prev);
                  setNewCategoryBrand(prev => ({
                    ...prev,
                    brands: selectedBrands,
                  }));
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setshowBrandModal(false)}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={async () => {
              try {
                const brandPayload = {
                  ...newCategoryBrand,
                  categoryId: parseInt(categoryId!), // ensure categoryId is present
                };

                // TODO: replace with your real API
                const res = await onCategoryBrandCreate(brandPayload);
                if (res.status === 0) {
                  toast.success(res.msg);
                  categoryDetailGet(); // reload from backend

                  setshowBrandModal(false);
                } else {
                  toast.error(res.msg);
                }

              } catch (e) {
                console.error(e);
                alert("Failed to add brand");
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CategoryDetailPage;